"use client";

import { useCallback, useEffect, useState, useRef } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { formatDate, type Locale } from "@/config/locale";
import { cn } from "@/lib/utils";
import { Button } from "@/ui/components/ui/button";
import { SUPPORT_TOPICS, type SupportTopic, topicRequiresOrderNumber } from "@/lib/support-outbox/topics";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Topic keys come from the same taxonomy the API validates against
// (normalizeTopic) — a value the dropdown can render is always one the
// server accepts. The legacy "orders" value has no option here anymore, but
// route.ts still aliases it for old deep links, so it is not rejected.
const TOPICS = SUPPORT_TOPICS;
type ContactTopic = SupportTopic;

type FormStatus = "idle" | "loading" | "success" | "error";
type FormErrorKey = "formError" | "formErrorRateLimit" | "formErrorCaptcha" | "formErrorCaptchaPending";

// Public site key for the Cloudflare Turnstile widget. Read directly at
// module scope in this "use client" file — the same pattern
// src/lib/auth/auth-provider.tsx uses for NEXT_PUBLIC_SALEOR_API_URL, which
// is proven to work in production. The one documented exception in this repo
// is the checkout module's separately-bundled client code (see
// src/config/paygateto.ts's PAYGATETO_RESUME_ENABLED comment, "Turbopack
// does not inline NEXT_PUBLIC_* into the checkout client bundle"); this file
// lives in the main app bundle, not checkout, so that gotcha does not apply
// here. An empty/unset key simply skips rendering the widget below — no
// token is ever sent, and verifyTurnstile treats that as "unavailable"
// server-side only when TURNSTILE_SECRET_KEY is also unset. If the secret
// IS configured without the site key reaching the browser, every submission
// would be rejected — the two env vars must be deployed together.
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";

interface TurnstileRenderOptions {
	sitekey: string;
	callback: (token: string) => void;
	"expired-callback"?: () => void;
	"error-callback"?: () => void;
}

interface TurnstileApi {
	render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
	reset: (widgetId: string) => void;
	remove: (widgetId: string) => void;
}

declare global {
	interface Window {
		turnstile?: TurnstileApi;
	}
}

// Client-side mirror of the server-side email regex (route.ts:53) so an
// invalid address never burns one of the 3 requests/10min the shared
// `contact:${ip}` rate-limit bucket allows before the body is even read.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Visual order of the validated fields, top to bottom, used both to decide
// which field to focus first and to render the error summary in the same
// order the user encounters them. "orderNumber" only renders once a topic
// that requires it is selected (topicRequiresOrderNumber) — focusErrorField
// no-ops if the element isn't mounted.
const FIELD_ORDER = [
	{ key: "name", id: "contact-name" },
	{ key: "email", id: "contact-email" },
	{ key: "orderNumber", id: "support-order-number" },
	{ key: "topic", id: "contact-topic" },
	{ key: "message", id: "contact-message" },
	{ key: "consent", id: "contact-consent" },
] as const;

export interface ContactOrderOption {
	id: string;
	number: string;
	created: string;
}

export interface ContactFormProps {
	locale: string;
	channel: string;
	mode?: "public" | "account";
	defaultName?: string;
	defaultEmail?: string;
	orders?: ContactOrderOption[];
	initialOrderId?: string | null;
}

// ---------------------------------------------------------------------------
// Field label key helper
// ---------------------------------------------------------------------------

// snake_case topic values (e.g. "order_status") don't naively title-case into
// their camelCase message keys, so this is an explicit table rather than a
// string transform.
const TOPIC_MESSAGE_KEYS: Record<ContactTopic, string> = {
	order_status: "formTopicOrderStatus",
	presales: "formTopicPresales",
	returns: "formTopicReturns",
	partnership: "formTopicPartnership",
	other: "formTopicOther",
};

function topicKey(topic: ContactTopic): string {
	return TOPIC_MESSAGE_KEYS[topic];
}

function initialOrderNumberFromProps(
	orders: ContactOrderOption[] | undefined,
	initialOrderId: string | null | undefined,
): string {
	if (!initialOrderId || !orders?.length) return "";
	const o = orders.find((x) => x.id === initialOrderId);
	return o?.number ?? "";
}

function initialSelectedOrderId(
	orders: ContactOrderOption[] | undefined,
	initialOrderId: string | null | undefined,
): string {
	if (!initialOrderId || !orders?.length) return "";
	return orders.some((x) => x.id === initialOrderId) ? initialOrderId : "";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ContactForm({
	locale,
	channel,
	mode = "public",
	defaultName = "",
	defaultEmail = "",
	orders,
	initialOrderId,
}: ContactFormProps) {
	const t = useTranslations("contact");
	const ta = useTranslations("account");
	const privacyHref = `/${locale}/${channel}/privacy`;
	const isAccount = mode === "account";

	const [name, setName] = useState(defaultName);
	const [email, setEmail] = useState(defaultEmail);
	const [topic, setTopic] = useState<ContactTopic | "">("");
	const [message, setMessage] = useState("");
	const [consent, setConsent] = useState(false);
	const [status, setStatus] = useState<FormStatus>("idle");
	const [errorKey, setErrorKey] = useState<FormErrorKey>("formError");
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [appendedToExisting, setAppendedToExisting] = useState(false);
	const idempotencyKeyRef = useRef<string | null>(null);

	const [orderNumber, setOrderNumber] = useState(() => initialOrderNumberFromProps(orders, initialOrderId));
	const [selectedOrderId, setSelectedOrderId] = useState(() =>
		initialSelectedOrderId(orders, initialOrderId),
	);
	const [noOrderNumber, setNoOrderNumber] = useState(false);

	const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
	const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
	const turnstileWidgetIdRef = useRef<string | null>(null);

	// Loads the Cloudflare script once, renders the widget into the ref'd
	// container, and tears the widget down on unmount. No-ops entirely when
	// the site key isn't configured (see the TURNSTILE_SITE_KEY comment above).
	useEffect(() => {
		if (!TURNSTILE_SITE_KEY) return;

		let cancelled = false;

		function renderWidget() {
			if (cancelled || !turnstileContainerRef.current || turnstileWidgetIdRef.current) return;
			const turnstile = window.turnstile;
			if (!turnstile || !TURNSTILE_SITE_KEY) return;
			turnstileWidgetIdRef.current = turnstile.render(turnstileContainerRef.current, {
				sitekey: TURNSTILE_SITE_KEY,
				callback: (token) => setTurnstileToken(token),
				"expired-callback": () => setTurnstileToken(null),
				"error-callback": () => setTurnstileToken(null),
			});
		}

		let script = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
		if (window.turnstile) {
			renderWidget();
		} else {
			if (!script) {
				script = document.createElement("script");
				script.id = TURNSTILE_SCRIPT_ID;
				script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
				script.async = true;
				script.defer = true;
				document.head.appendChild(script);
			}
			script.addEventListener("load", renderWidget);
		}

		return () => {
			cancelled = true;
			script?.removeEventListener("load", renderWidget);
			if (turnstileWidgetIdRef.current) {
				window.turnstile?.remove(turnstileWidgetIdRef.current);
				turnstileWidgetIdRef.current = null;
			}
		};
	}, []);

	const focusErrorField = useCallback((fieldId: string) => {
		const element = document.getElementById(fieldId);
		if (!element) return;
		element.scrollIntoView({ behavior: "smooth", block: "center" });
		if ("focus" in element && typeof element.focus === "function") {
			element.focus({ preventScroll: true });
		}
	}, []);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();

		const trimmedName = name.trim();
		const trimmedEmail = email.trim();
		const trimmedMessage = message.trim();
		const trimmedOrder = orderNumber.trim();

		// Mirror route.ts:40-70 exactly. Keep noValidate on the <form> — native
		// constraint validation only fires minLength on a "dirty" value (these
		// are React-controlled inputs written programmatically) and its bubble
		// text follows the browser UI language, not the next-intl locale.
		const newErrors: Record<string, string> = {};

		if (!trimmedName) {
			newErrors.name = t("formErrorNameRequired");
		} else if (trimmedName.length > 100) {
			newErrors.name = t("formErrorNameTooLong");
		}

		if (!trimmedEmail) {
			newErrors.email = t("formErrorEmailRequired");
		} else if (!EMAIL_REGEX.test(trimmedEmail)) {
			newErrors.email = t("formErrorEmailInvalid");
		}

		if (trimmedOrder && (trimmedOrder.length > 64 || /[\r\n]/.test(trimmedOrder))) {
			newErrors.orderNumber = t("formErrorOrderNumberInvalid");
		} else if (topic && topicRequiresOrderNumber(topic) && !trimmedOrder && !noOrderNumber) {
			newErrors.orderNumber = t("formErrorOrderNumberRequired");
		}

		if (!topic) {
			newErrors.topic = t("formErrorTopicRequired");
		}

		// route.ts:43 trims before measuring — a whitespace-only message must
		// fail here exactly like it would server-side.
		if (trimmedMessage.length < 10) {
			newErrors.message = t("formErrorMessageTooShort");
		} else if (trimmedMessage.length > 2000) {
			newErrors.message = t("formErrorMessageTooLong");
		}

		if (!consent) {
			newErrors.consent = t("formErrorConsentRequired");
		}

		setErrors(newErrors);

		if (Object.keys(newErrors).length > 0) {
			const firstInvalid = FIELD_ORDER.find(({ key }) => newErrors[key]);
			if (firstInvalid) {
				focusErrorField(firstInvalid.id);
			}
			return;
		}

		// Turnstile resets its token to null on every submit attempt, including a
		// failed one (see the finally block below) - there is a real window between
		// page load/reset and the widget's callback firing. Gate the handler, not the
		// button: a widget that never loads at all (privacy blocker, corporate proxy,
		// CSP, invalid sitekey, domain mismatch) would otherwise leave turnstileToken
		// null forever and a disabled button wedged shut permanently - trading the
		// original 10-minute rate-limit lockout for total silent message loss, the
		// exact failure class this whole branch exists to prevent. Skipping the fetch
		// call here is what actually protects the 3 requests/10min IP rate limit; the
		// customer just needs to know to try again once the check finishes.
		if (Boolean(TURNSTILE_SITE_KEY) && !turnstileToken) {
			setErrorKey("formErrorCaptchaPending");
			setStatus("error");
			return;
		}

		setStatus("loading");
		const payload: Record<string, unknown> = {
			name,
			email,
			topic,
			message,
			consent,
			locale,
		};
		if (trimmedOrder) {
			payload.orderNumber = trimmedOrder;
		}
		if (noOrderNumber) {
			payload.noOrderNumber = true;
		}
		// Field name is part of the API contract (route.ts reads
		// body.turnstileToken exactly) — null is valid and means "no widget
		// configured client-side" or "not yet solved".
		payload.turnstileToken = turnstileToken;
		if (isAccount) {
			payload.contactSource = "account_support";
		}
		try {
			idempotencyKeyRef.current ??= globalThis.crypto.randomUUID();
			const res = await fetch("/api/contact", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"Idempotency-Key": idempotencyKeyRef.current,
				},
				body: JSON.stringify(payload),
			});
			if (!res.ok) {
				const data = (await res.json()) as { error?: string };
				setErrorKey(
					data?.error === "too_many_requests"
						? "formErrorRateLimit"
						: data?.error === "captcha_failed"
							? "formErrorCaptcha"
							: "formError",
				);
				setStatus("error");
				return;
			}
			const data = (await res.json()) as { appendedToExisting?: boolean };
			setAppendedToExisting(data?.appendedToExisting === true);
			setStatus("success");
		} catch {
			setErrorKey("formError");
			setStatus("error");
		} finally {
			// Turnstile tokens are single-use — reset so a retry (rate limit,
			// network error, validation bounce from the server) gets a fresh one.
			if (turnstileWidgetIdRef.current) {
				window.turnstile?.reset(turnstileWidgetIdRef.current);
			}
			setTurnstileToken(null);
		}
	}

	function onOrderSelectChange(id: string) {
		setSelectedOrderId(id);
		if (id === "") {
			setOrderNumber("");
		} else {
			const o = orders?.find((x) => x.id === id);
			setOrderNumber(o?.number ?? "");
		}
	}

	function onOrderNumberInput(value: string) {
		setOrderNumber(value);
		setSelectedOrderId("");
	}

	// ---- Success state ----
	if (status === "success" && topic) {
		const label = t(topicKey(topic) as Parameters<typeof t>[0]);
		return (
			<div className="rounded-xl border border-border bg-card p-6 shadow-sm">
				<div className="flex flex-col items-center gap-4 py-6 text-center">
					<div className="bg-accent/10 flex h-14 w-14 items-center justify-center rounded-full">
						<CheckCircle2 className="h-7 w-7 text-accent" aria-hidden="true" />
					</div>
					<div className="space-y-1">
						<p className="text-base font-semibold text-foreground">{t("formSuccess")}</p>
						<p className="mx-auto max-w-xs text-sm leading-relaxed text-muted-foreground">
							{appendedToExisting
								? t("formSuccessAppendedDesc")
								: t("formSuccessDesc", { name, topic: label })}
						</p>
					</div>
				</div>
			</div>
		);
	}

	const formTitle = isAccount ? ta("supportFormTitle") : t("formTitle");

	// ---- Form ----
	return (
		<div className="rounded-xl border border-border bg-card p-6 shadow-sm">
			<h2 className="mb-6 text-base font-semibold text-foreground">{formTitle}</h2>

			{Object.keys(errors).length > 0 && (
				<div
					role="alert"
					aria-labelledby="contact-error-summary-title"
					className="border-destructive/50 bg-destructive/10 mb-5 rounded-lg border p-4"
				>
					<p id="contact-error-summary-title" className="font-medium text-destructive">
						{t("formErrorSummaryTitle", { count: Object.keys(errors).length })}
					</p>
					<ul className="mt-2 space-y-1 text-sm">
						{FIELD_ORDER.filter(({ key }) => errors[key]).map(({ key, id }) => (
							<li key={key}>
								<button
									type="button"
									onClick={() => focusErrorField(id)}
									className="text-left text-destructive underline underline-offset-2"
								>
									{errors[key]}
								</button>
							</li>
						))}
					</ul>
				</div>
			)}

			<form onSubmit={(e) => void handleSubmit(e)} className="space-y-5" noValidate>
				{/* Name + Email — side by side on sm+ */}
				<div className="grid gap-5 sm:grid-cols-2">
					<div className="space-y-1.5">
						<label htmlFor="contact-name" className="block text-sm font-medium text-foreground">
							{t("formName")}
						</label>
						<input
							id="contact-name"
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							required
							maxLength={100}
							autoComplete="name"
							className="w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground transition-colors placeholder:text-muted-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
						/>
					</div>

					<div className="space-y-1.5">
						<label htmlFor="contact-email" className="block text-sm font-medium text-foreground">
							{t("formEmail")}
						</label>
						<input
							id="contact-email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
							autoComplete="email"
							className="w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground transition-colors placeholder:text-muted-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
						/>
					</div>
				</div>

				{isAccount && orders && orders.length > 0 && (
					<div className="space-y-1.5">
						<label htmlFor="support-order-select" className="block text-sm font-medium text-foreground">
							{ta("supportOrderSelectLabel")}
						</label>
						<select
							id="support-order-select"
							value={selectedOrderId}
							onChange={(e) => onOrderSelectChange(e.target.value)}
							className={cn(
								"w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring",
								selectedOrderId === "" ? "text-muted-foreground" : "text-foreground",
							)}
						>
							<option value="">{ta("supportOrderSelectPlaceholder")}</option>
							{orders.map((o) => {
								const shortDate = formatDate(
									new Date(o.created),
									{
										year: "numeric",
										month: "short",
										day: "numeric",
									},
									locale as Locale,
								);
								return (
									<option key={o.id} value={o.id}>
										{ta("supportOrderSelectOption", { number: o.number, date: shortDate })}
									</option>
								);
							})}
						</select>
					</div>
				)}

				{topic && topicRequiresOrderNumber(topic) && (
					<div className="space-y-1.5">
						<label htmlFor="support-order-number" className="block text-sm font-medium text-foreground">
							{t("formOrderNumberLabel")}
						</label>
						<input
							id="support-order-number"
							type="text"
							value={orderNumber}
							onChange={(e) => onOrderNumberInput(e.target.value)}
							maxLength={64}
							autoComplete="off"
							className="w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground transition-colors placeholder:text-muted-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
						/>
						<p className="text-xs text-muted-foreground">{t("formOrderNumberHint")}</p>
						<label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
							<input
								id="support-no-order-number"
								type="checkbox"
								checked={noOrderNumber}
								onChange={(e) => setNoOrderNumber(e.target.checked)}
								className="h-4 w-4 shrink-0 rounded border border-border bg-background accent-primary"
							/>
							<span>{t("formNoOrderNumber")}</span>
						</label>
					</div>
				)}

				{/* Topic */}
				<div className="space-y-1.5">
					<label htmlFor="contact-topic" className="block text-sm font-medium text-foreground">
						{t("formTopic")}
					</label>
					<select
						id="contact-topic"
						value={topic}
						onChange={(e) => setTopic(e.target.value as ContactTopic | "")}
						required
						className={cn(
							"w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring",
							topic === "" ? "text-muted-foreground" : "text-foreground",
						)}
					>
						<option value="" disabled>
							{t("formTopicPlaceholder")}
						</option>
						{TOPICS.map((key) => (
							<option key={key} value={key}>
								{t(topicKey(key) as Parameters<typeof t>[0])}
							</option>
						))}
					</select>
				</div>

				{/* Message */}
				<div className="space-y-1.5">
					<label htmlFor="contact-message" className="block text-sm font-medium text-foreground">
						{t("formMessage")}
					</label>
					<textarea
						id="contact-message"
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						required
						minLength={10}
						maxLength={2000}
						rows={5}
						className="min-h-[120px] w-full resize-y rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground transition-colors placeholder:text-muted-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
					/>
				</div>

				{/* Consent */}
				<label className="flex cursor-pointer items-start gap-3">
					<input
						id="contact-consent"
						type="checkbox"
						checked={consent}
						onChange={(e) => setConsent(e.target.checked)}
						required
						className="mt-0.5 h-4 w-4 shrink-0 rounded border border-border bg-background accent-primary"
					/>
					<span className="text-xs leading-relaxed text-muted-foreground">
						{t("formConsentPrefix")}
						<Link href={privacyHref} className="underline underline-offset-2 hover:text-foreground">
							{t("formConsentPrivacy")}
						</Link>
						{t("formConsentSuffix")}
					</span>
				</label>

				{/* Turnstile — renders only once NEXT_PUBLIC_TURNSTILE_SITE_KEY is
					configured; see the TURNSTILE_SITE_KEY comment above. */}
				{TURNSTILE_SITE_KEY && <div ref={turnstileContainerRef} className="flex justify-center" />}

				{/* Error message */}
				{status === "error" && (
					<p className="text-xs text-destructive" role="alert">
						{t(errorKey)}
					</p>
				)}

				{/* Submit */}
				{/* Gated in the handler above, not here. A disabled attribute that only
					lifts once turnstileToken resolves looks safer, but if the Cloudflare
					script never loads at all (privacy blocker, corporate proxy, CSP, invalid
					sitekey, domain mismatch) the token never arrives and the button never
					re-enables - a permanently dead submit button with no explanation on
					screen. handleSubmit blocks the fetch call instead, which is what
					actually protects the 3 requests/10min IP rate limit, and shows an inline
					message telling the customer to retry in a moment. */}
				<Button type="submit" variant="default" className="w-full" disabled={status === "loading"}>
					{status === "loading" ? (
						<span className="flex items-center gap-2">
							<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
							{t("formSubmitting")}
						</span>
					) : (
						<span className="flex items-center gap-2">
							<Send className="h-4 w-4" aria-hidden="true" />
							{t("formSubmit")}
						</span>
					)}
				</Button>
			</form>
		</div>
	);
}
